/**
 * @description Hook utilizado para manipular as entidades ExameRealizado
 * @author Diego Sabino Amorim de Araújo
 * @file useExameRealizadoService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import { useDispatch } from 'react-redux'
import actionTypes from 'utils/actionTypes'
import useRouter from 'utils/useRouter'
import { downloadURL } from 'utils/helpers'
import { useFetch } from './useFetch'

export const useExameRealizadoService = () => {
  const fetch = useFetch()
  const router = useRouter()
  const dispatch = useDispatch()

  const setProcessandoEditorGlobal = async (isProcessando) => {
    dispatch({
      type: actionTypes.SET_PROCESSANDO_EDITOR,
      isProcessando,
    })
  }
  const listarExameRealizadoEditorGlobal = async (id) => {
    setProcessandoEditorGlobal(true)
    const response = await fetch.get(`/examesRealizados/${id}/comFotos`)
    dispatch({
      type: actionTypes.SET_EXAME_REALIZADO_EDITOR_DOCUMENTO,
      exameRealizadoEditorDocumento: response.data.exameRealizado,
    })
    setProcessandoEditorGlobal(false)
  }

  const listarExameRealizadoDetalhadoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/detalharExameRealizado/${id}`)
      return response.data.exameRealizado
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const construirDocumentoExameRealizado = async (idExameRealizado, tipoExameRealizado) => {
    await fetch.get(`/examesRealizados/gerarDocumento/${idExameRealizado}`)
    router.history.push({
      pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${idExameRealizado}`,
      state: { IgnoneUploadImagem: true, tipoExame: tipoExameRealizado },
    })
  }

  const responderQuesitosEGerarDocumento = async (idExameRealizado, dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    if (dados.respostasQuesitos) {
      await fetch.postOpen('/examesRealizados/alterarRespostas', {
        listaRespostas: dados.respostasQuesitos,
      })
    }
    const exameRealizado = await listarExameRealizadoDetalhadoAsync(idExameRealizado)
    await construirDocumentoExameRealizado(idExameRealizado, exameRealizado.tipo)
    if (setProcessando) setProcessando(false)
  }

  const listarRespostasQuesitosDeExameRealizadoAsync = async (idExameRealizado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/respostasQuesitos/paraExame/${idExameRealizado}`)
      return response.data.respostasQuesitos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const openPDFInNewTab = (url, nome) => {
    window.open(url, 'nome_especifico')
  }

  const downloadArquivoDocumento = async (idExame, fileName, setProcessando, registrarDownload = true, tipoDocumento) => {
    try {
      if (setProcessando) setProcessando(true)

      let response = null
      if (tipoDocumento === 'EXAME_SOLICITADO') {
        response = await fetch.getWithOptionsWithoutFeedback(`/exameSolicitado/documento/pdf/${idExame}`, {
          responseType: 'blob',
        })
      } else {
        response = await fetch.getWithOptionsWithoutFeedback(`/examesRealizados/download/documento/${idExame}/${registrarDownload}`, {
          responseType: 'blob',
        })
      }

      const fileURL = URL.createObjectURL(response.data)

      openPDFInNewTab(fileURL, fileName)
    } catch (err) {
      throw err
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const downloadArquivoErrata = async (idErrata, fileName, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.getWithOptions(`/examesRealizados/download/documentoerrata/${idErrata}`, {
        responseType: 'blob',
      })
      const fileURL = URL.createObjectURL(response.data)
      openPDFInNewTab(fileURL, fileName)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const gerarDocumento = async (id, setProcessando, data) => {
    try {
      if (setProcessando) setProcessando(true)
      if (data) {
        await fetch.postOpen(`/examesRealizados/gerarDocumento/${id}`, data)
      } else {
        await fetch.get(`/examesRealizados/gerarDocumento/${id}`)
      }
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const alterarRespostas = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      if (dados.respostasQuesitos) {
        await fetch.postOpen('/examesRealizados/alterarRespostas', {
          listaRespostas: dados.respostasQuesitos,
        })
      }
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const salvarDocumento = async (id, texto, fotosDocumento, setProcessando, assinarExame) => {
    try {
      if (setProcessando) setProcessando(true)
      const data = {
        idExameRealizado: id,
        texto,
        fotosEditor: fotosDocumento,
        assinarExame: assinarExame,
      }
      await fetch.putOpen(`/examesRealizados/salvarDocumento`, data)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  // eslint-disable-next-line
  const finalizarDocumento = async (id, idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const data = {
        idExameRealizado: id,
      }
      await fetch.putOpen('/examesRealizados/finalizarDocumento', data)
      router.history.push(`/casos/caso/solicitacao/exameSolicitado/detalhar/${idExameSolicitado}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const setarExameParaEndamento = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/examesRealizados/setarParaEmAndamento/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const finalizarDocumentoPorUpload = async (id, arquivos, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const data = {
        idExameRealizado: id,
        idArquivo: arquivos && arquivos.length ? arquivos[0] : null,
      }
      await fetch.putOpen('/examesRealizados/finalizarDocumentoUpload', data)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const visualizarDocumento = async (idExameRealizado, texto, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.getWithOptions(`/examesRealizados/getPdf/${idExameRealizado}`, {
        responseType: 'blob',
      })

      const fileURL = URL.createObjectURL(response.data)

      openPDFInNewTab(fileURL, fileName)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosDeCasoAsync = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/deCaso/${idCaso}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosDeCasoPorTiposAsync = async (idCaso, codigosTipos, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        codigosTipos,
      }
      const response = await fetch.postOpenWithoutFeedback(`/examesRealizados/deCaso/porTipos/${idCaso}`, dados)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarExameRealizado = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.put(`/examesRealizados/editar/${id}`, values)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/examesRealizados')
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosPeloNumeroDoExameASync = async (numero, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/filtrar/pelonumero/${numero}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosFiltradosAsync = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const filtros = {
        setores: dados.setores ? dados.setores.map((setor) => setor.id) : null,
        tipos: dados.tipos ? dados.tipos.map((tipo) => tipo.id) : null,
        status: dados.status && dados.status.length ? dados.status : null,
        usuarioRequisitante: dados.usuarioRequisitante ? dados.usuarioRequisitante.map((usuario) => usuario.id) : null,
        dataSolicitacaoInicio: dados.dataSolicitacaoChecked && dados.dataSolicitacaoInicio ? dados.dataSolicitacaoInicio : null,
        dataSolicitacaoFinal: dados.dataSolicitacaoChecked && dados.dataSolicitacaoFinal ? dados.dataSolicitacaoFinal : null,
        dataCriacaoInicio: dados.dataCriacaoChecked && dados.dataCriacaoInicio ? dados.dataCriacaoInicio : null,
        dataCriacaoFinal: dados.dataCriacaoChecked && dados.dataCriacaoFinal ? dados.dataCriacaoFinal : null,
        dataAtribuicaoInicio: dados.dataAtribuicaoChecked && dados.dataAtribuicaoInicio ? dados.dataAtribuicaoInicio : null,
        dataAtribuicaoFinal: dados.dataAtribuicaoChecked && dados.dataAtribuicaoFinal ? dados.dataAtribuicaoFinal : null,
        dataFinalizacaoInicio: dados.dataFinalizacaoChecked && dados.dataFinalizacaoInicio ? dados.dataFinalizacaoInicio : null,
        dataFinalizacaoFinal: dados.dataFinalizacaoChecked && dados.dataFinalizacaoFinal ? dados.dataFinalizacaoFinal : null,
        examinadores: dados.examinadores ? dados.examinadores.map((usuario) => usuario.id) : null,
        numeroProcedimento: dados.numeroProcedimento && dados.numeroProcedimento.length ? dados.numeroProcedimento : null,
        numeroDocumento: dados.numeroDocumento && dados.numeroDocumento.length ? dados.numeroDocumento : null,
        dataPublicacaoInicio: dados.dataPublicacaoChecked && dados.dataPublicacaoInicio ? dados.dataPublicacaoInicio : null,
        dataPublicacaoFinal: dados.dataPublicacaoChecked && dados.dataPublicacaoFinal ? dados.dataPublicacaoFinal : null,
      }

      const response = await fetch.postOpenWithoutFeedback(`/examesRealizados/filtrar`, filtros)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const publicarExameRealizado = async (id, setProcessando, dadosPrivacidade) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/examesRealizados/publicar/${id}`, dadosPrivacidade)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosPorStatusDeUsuarioIntervaloDeTempo = async (status, intervalo, setExames, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/deusuario/contagem/${intervalo}/${status}`)
      setExames(response.data.examesRealizados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosDeUsuarioIntervaloDeTempo = async (intervalo, setExames, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/deusuario/contagem/${intervalo}`)

      setExames(response.data.examesRealizados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarQuantidadeExamesRealizadosDeUsuarioPorTipoAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/deusuario/contagemportipo`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosNosCasosDeVestigioAsync = async (idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/nosCasos/deVestigio/${idVestigio}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosNoCasoDeCadaverAsync = async (idCadaver, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/noCaso/deCadaver/${idCadaver}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosNoCasoDePericiandoAsync = async (idPericiando, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/noCaso/dePericiando/${idPericiando}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesRealizadosNoCasoDeVeiculoAsync = async (idVeiculo, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/noCaso/deVeiculo/${idVeiculo}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cancelarExameRealizadoService = async (dados, idExameRealizado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.putOpen(`/examesRealizados/cancelarExame/${idExameRealizado}`, dados)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const assinarExame = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.put(`/examesRealizados/assinarExame/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const atualizarTemplateExame = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/examesRealizados/atualizarTemplate/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const adicionarArquivosExame = async (id, idsArquivos, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/examesRealizados/adicionarArquivos/${id}`, { idsArquivos })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const assinarEFinalizarErrata = async (idExameRealizado, texto, dadosIdentificacao, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/examesRealizados/cadastrarerrata/${idExameRealizado}`, { texto, dadosIdentificacao })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const filtrarVestigioPeloNumeroLikeString = async (numero, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/filtrar/likestring/${numero}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const filtrarIdentificadoresExamesPeloNumeroLikeString = async (numero, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/examesRealizados/filtrar/identificadores/likestring/${numero}`)
      return response.data.examesRealizados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const desvincularExaminador = async (idExameRealizado, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`examesRealizados/desvincularExaminador/${idExameRealizado}`, { examinadores: dados })
      await listarExameSolicitado(idExameRealizado, setProcessando)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    construirDocumentoExameRealizado,
    responderQuesitosEGerarDocumento,
    filtrarVestigioPeloNumeroLikeString,
    alterarRespostas,
    gerarDocumento,
    listarExameRealizadoEditorGlobal,
    listarExamesRealizadosDeCasoAsync,
    listarExameRealizadoDetalhadoAsync,
    listarExamesRealizadosAsync,
    listarExamesRealizadosPeloNumeroDoExameASync,
    salvarDocumento,
    finalizarDocumento,
    visualizarDocumento,
    finalizarDocumentoPorUpload,
    downloadArquivoDocumento,
    editarExameRealizado,
    listarExamesRealizadosFiltradosAsync,
    publicarExameRealizado,
    listarExamesRealizadosPorStatusDeUsuarioIntervaloDeTempo,
    listarExamesRealizadosDeUsuarioIntervaloDeTempo,
    listarQuantidadeExamesRealizadosDeUsuarioPorTipoAsync,
    listarExamesRealizadosDeCasoPorTiposAsync,
    listarExamesRealizadosNosCasosDeVestigioAsync,
    listarExamesRealizadosNoCasoDeCadaverAsync,
    listarExamesRealizadosNoCasoDePericiandoAsync,
    listarExamesRealizadosNoCasoDeVeiculoAsync,
    listarRespostasQuesitosDeExameRealizadoAsync,
    cancelarExameRealizadoService,
    assinarExame,
    setarExameParaEndamento,
    atualizarTemplateExame,
    adicionarArquivosExame,
    assinarEFinalizarErrata,
    downloadArquivoErrata,
    desvincularExaminador,
    filtrarIdentificadoresExamesPeloNumeroLikeString,
  }
}
